import { Box, Button, CircularProgress } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import strings from "global/constants/StringConstants";
import { PromoCampaign } from "helpers/promotionHelpers";
import upgradePlanTokens from "screens/Settings/MyPlan/UpgradePlan.tokens";
import {
  planBlurbSx,
  planChipSx,
  planBulletListSx,
  planBulletRowSx,
  planBulletTextSx,
  planCardSx,
  planCtaSx,
  planCycleNoteSx,
  planFooterSx,
  planHeadSx,
  planNameSx,
  planNoteSx,
  planPerSx,
  planPriceRowSx,
  planPriceSx,
  planPromoPriceSx,
  planPromoRenewSx,
  planPromoTotalLineSx,
  planPromoTotalSx,
  planRulePillSx,
  planStruckPriceSx,
  planTotalNoteSx,
  promoPillSx,
  recommendedBadgeSx,
} from "screens/Settings/MyPlan/UpgradePlan.styles";
import { UpgradePlanCard } from "screens/Settings/MyPlan/components/UpgradePlanHelper";
import { FeatureCheckIcon } from "./UpgradePlanIcons";

const t = upgradePlanTokens;

/** One column of the "Choose your plan" grid. */
const PlanCard = (props: {
  card: UpgradePlanCard;
  /** A seat check is running — hold selections until it resolves. */
  isBusy?: boolean;
  /** This is the card whose seat check is running; it shows the spinner. */
  isChecking?: boolean;
  /** Campaign whose discount `card.promo` carries — colours its pill. */
  promoCampaign?: PromoCampaign | null;
  onSelect: (plan: any) => void;
}) => {
  const { card } = props;
  const isActionable = card.isEligible && !card.isCurrent;
  const promo = props.promoCampaign && card.promo ? card.promo : null;

  const getCtaLabel = () => {
    if (card.isCurrent) return "Current plan";
    if (!card.isEligible) return "Not available";
    return promo ? strings.promotions.plan.claim : `Switch to ${card.name}`;
  };

  return (
    <Box
      sx={planCardSx(card.isRecommended, !card.isEligible)}
      aria-current={card.isCurrent ? "true" : undefined}
    >
      {card.isRecommended && (
        <Box component="span" sx={recommendedBadgeSx}>
          RECOMMENDED
        </Box>
      )}

      <Box sx={planHeadSx}>
        <Box component="span" sx={planChipSx(card.chip.tone)}>
          {card.chip.label}
        </Box>
        <Box component="h2" sx={planNameSx}>
          {card.name}
        </Box>
        <Box sx={planPriceRowSx}>
          {promo && (
            <Box component="s" sx={planStruckPriceSx}>
              {/* A strikethrough isn't announced, so say which is which. */}
              <Box component="span" sx={visuallyHidden}>
                {strings.promotions.plan.regularPriceLabel}
              </Box>
              {promo.regularPrice}
            </Box>
          )}
          <Box component="span" sx={promo ? planPromoPriceSx : planPriceSx}>
            {promo && (
              <Box component="span" sx={visuallyHidden}>
                {strings.promotions.plan.offerPriceLabel}
              </Box>
            )}
            {card.price}
          </Box>
          <Box component="span" sx={planPerSx}>
            {card.per}
          </Box>
        </Box>
        <Box component="span" sx={planCycleNoteSx}>
          {card.cycleNote}
        </Box>
        <Box component="span" sx={planBlurbSx}>
          {card.blurb}
        </Box>
        {promo && props.promoCampaign ? (
          <Box sx={planPromoTotalSx}>
            <Box component="span" sx={planPromoTotalLineSx}>
              {card.totalNote}
            </Box>
            <Box component="span" sx={promoPillSx(props.promoCampaign)}>
              {promo.pill}
            </Box>
            <Box component="span" sx={planPromoRenewSx}>
              {promo.renew}
            </Box>
          </Box>
        ) : (
          card.totalNote && (
            <Box component="span" sx={planTotalNoteSx}>
              {card.totalNote}
            </Box>
          )
        )}
      </Box>

      <Box component="span" sx={planRulePillSx}>
        {card.rule}
      </Box>

      <Box sx={planBulletListSx}>
        {card.bullets.map((bullet: string, index: number) => (
          <Box key={index} sx={planBulletRowSx}>
            <FeatureCheckIcon
              sx={{ color: t.color.bulletCheck, mt: t.space.bulletIconTop }}
            />
            <Box component="span" sx={planBulletTextSx}>
              {bullet}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={planFooterSx}>
        <Button
          id={`upgrade-plan-cta-${card.id}`}
          disableElevation
          disabled={!isActionable || props.isBusy}
          aria-busy={props.isChecking}
          sx={planCtaSx(card.isRecommended, isActionable)}
          onClick={() => props.onSelect(card.plan)}
        >
          {/* The seat check runs before checkout opens; without this the card
              just goes quiet for the length of the round trip. */}
          {props.isChecking ? (
            <CircularProgress
              size={18}
              sx={{ color: card.isRecommended ? t.color.onAccent : "inherit" }}
            />
          ) : (
            getCtaLabel()
          )}
        </Button>
        {card.note && (
          <Box component="span" sx={planNoteSx}>
            {card.note}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlanCard;
